import { useMutation, useQueryClient } from "react-query";

import axios from "axios";

import { projectServiceEndpoint } from "api/project";

import { PROJECT_LAYER_LIST_QUERY_KEYS } from "components/project/layers/queries";
import {
  ORGANIZATION_SHAPEFILES_QUERY_KEYS,
  PROJECT_SHAPEFILES_QUERY_KEYS,
  SYSTEM_SHAPEFILES_QUERY_KEYS
} from "components/project/shapefiles/queries";
import { usePurgeDeletedShapefilesFromDefaults } from "components/user-settings/hooks";
import {
  DEFAULTS_PURGE_FAILURE_MESSAGES,
  reportDefaultsPurgeFailure
} from "components/user-settings/hooks/reportDefaultsPurgeFailure";

interface IUseDeleteProjectMutationParams {
  onError?: (error) => void;
  onMutate?: () => void;
  onSettled?: () => void;
  onSuccess?: (data) => void;
  projectId: string;
}

interface IDeleteProjectShapefileParams {
  shapefileId: string;
  projectId?: string;
}

export async function deleteProjectShapefile(params: IDeleteProjectShapefileParams) {
  const url =
    params.projectId === "system"
      ? `${projectServiceEndpoint}/system-shapefile/${params.shapefileId}`
      : `${projectServiceEndpoint}/shapefile/${params.shapefileId}`;

  return await axios.delete(url);
}

export function useDeleteProjectShapefileMutation({
  onError,
  onMutate,
  onSettled,
  onSuccess,
  projectId
}: IUseDeleteProjectMutationParams) {
  const queryClient = useQueryClient();
  const purgeDeletedShapefilesFromDefaults = usePurgeDeletedShapefilesFromDefaults();

  return useMutation(
    (params: IDeleteProjectShapefileParams) => deleteProjectShapefile(params),
    {
      onMutate: onMutate && onMutate,
      onSuccess: (data, variables) => {
        if (projectId === "system") {
          queryClient.invalidateQueries({
            queryKey: [SYSTEM_SHAPEFILES_QUERY_KEYS.systemShapefiles]
          });
        } else if (projectId === "organization") {
          queryClient.invalidateQueries({
            queryKey: [ORGANIZATION_SHAPEFILES_QUERY_KEYS.organizationShapefiles]
          });
          queryClient.invalidateQueries({
            queryKey: [PROJECT_SHAPEFILES_QUERY_KEYS.currentProjectShapefiles]
          });

          queryClient.invalidateQueries({
            queryKey: [PROJECT_LAYER_LIST_QUERY_KEYS.currentProjectLayerList]
          });
        }

        queryClient.invalidateQueries({
          queryKey: [PROJECT_SHAPEFILES_QUERY_KEYS.currentProjectShapefiles, projectId]
        });

        queryClient.invalidateQueries({
          queryKey: [PROJECT_LAYER_LIST_QUERY_KEYS.currentProjectLayerList, projectId]
        });

        // The user's saved focus/tooltip lists store raw property keys, so a
        // deleted shapefile's fields linger there and keep being requested
        // (EVA-126). Best-effort - never block the caller's onSuccess below.
        try {
          purgeDeletedShapefilesFromDefaults([variables?.shapefileId]);
        } catch (error) {
          reportDefaultsPurgeFailure(
            DEFAULTS_PURGE_FAILURE_MESSAGES.shapefilePurgeFailed,
            error
          );
        }

        onSuccess && onSuccess(data);
      },
      onError: (error) => {
        onError && onError(error);
      },
      onSettled: onSettled && onSettled
    }
  );
}
